// ============================================================
// livePriceStore - Fase 4.
//
// Cliente WebSocket al PROXY del backend (GET /api/live-monitor/prices).
// El backend mantiene 1 sola conexion upstream al BarChart broadcast
// server (ws://98.88.106.157:8765) y broadcastea a clientes auth.
//
// Por que pasar por el backend (vs conectar directo al upstream):
//   - Auth: el upstream WS no tiene auth. El proxy exige cookie de sesion.
//   - 1 conexion upstream para todos los users (no N).
//   - Cambio de IP del upstream se hace via env var, sin rebuild del frontend.
//   - En prod (https://) la conexion es wss:// al mismo dominio del API
//     (gratis con el TLS del backend; sin proxy adicional en Caddy).
//
// Protocolo:
//   cliente -> server: { type: 'SUBSCRIBE', symbols: ['NQM6', ...] }
//   server  -> cliente: { type: 'CANDLE', candle: { symbol, close, ... } }
//   server  -> cliente: { type: 'STATUS', upstream: '...' }   (estado upstream)
// ============================================================

const RECONNECT_INITIAL_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

export interface LivePrice {
  symbol: string;
  price: number;
  timestamp: Date;
}

interface CandleMessage {
  symbol: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  timestamp: number;
  time: string;
  sessionId?: string;
}

type ServerMessage =
  | { type: 'CANDLE'; candle: CandleMessage }
  | { type: 'STATUS'; upstream: string };

type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

function defaultWsUrl(): string {
  // Mismo host que la pagina actual (Vite proxy en dev; mismo dominio en prod).
  if (typeof window === 'undefined') return 'ws://localhost:5173/api/live-monitor/prices';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/api/live-monitor/prices`;
}

class LivePriceStore {
  prices = $state<Map<string, LivePrice>>(new Map());
  lastMessage = $state<Date | null>(null);
  status = $state<ConnectionStatus>('idle');
  upstreamStatus = $state<string>('idle');

  #symbols: Set<string> = new Set();
  #ws: WebSocket | null = null;
  #stopped = true;
  #reconnectAttempt = 0;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #url: string = defaultWsUrl();

  setSymbols(symbols: Iterable<string>): void {
    const next = new Set<string>();
    for (const s of symbols) {
      if (s && typeof s === 'string') next.add(s);
    }

    // Si cambio el set, mandamos SUBSCRIBE con el nuevo set.
    const changed =
      next.size !== this.#symbols.size || [...next].some((s) => !this.#symbols.has(s));
    this.#symbols = next;

    if (changed) {
      this.#sendSubscribe();
    }

    // Limpiar prices de simbolos que ya no estan activos.
    const cleaned = new Map<string, LivePrice>();
    for (const [k, v] of this.prices) {
      if (next.has(k)) cleaned.set(k, v);
    }
    this.prices = cleaned;
  }

  /**
   * Add-only: suma symbols al set actual sin borrar prices ni desuscribir
   * los que ya estaban. Lo usan paginas como LiveMonitor que descubren
   * symbols antes que el refresh global del AppShell, para evitar el race
   * "WS no suscripto -> price.get(symbol) undefined" en el primer render.
   */
  addSymbols(symbols: Iterable<string>): void {
    let added = false;
    for (const s of symbols) {
      if (s && typeof s === 'string' && !this.#symbols.has(s)) {
        this.#symbols.add(s);
        added = true;
      }
    }
    if (added) this.#sendSubscribe();
  }

  start(): void {
    if (!this.#stopped) return;
    this.#stopped = false;
    this.#connect();
  }

  stop(): void {
    this.#stopped = true;
    if (this.#reconnectTimer) {
      clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = null;
    }
    if (this.#ws) {
      this.#ws.onopen = null;
      this.#ws.onmessage = null;
      this.#ws.onerror = null;
      this.#ws.onclose = null;
      try {
        this.#ws.close();
      } catch {
        /* noop */
      }
      this.#ws = null;
    }
    this.status = 'closed';
  }

  #sendSubscribe(): void {
    if (!this.#ws || this.#ws.readyState !== WebSocket.OPEN) return;
    try {
      this.#ws.send(JSON.stringify({ type: 'SUBSCRIBE', symbols: [...this.#symbols] }));
    } catch (err) {
      console.warn('[livePrice] send SUBSCRIBE fallo', err);
    }
  }

  #connect(): void {
    if (this.#stopped) return;
    this.status = this.#reconnectAttempt === 0 ? 'connecting' : 'reconnecting';

    let ws: WebSocket;
    try {
      ws = new WebSocket(this.#url);
    } catch (err) {
      console.error('[livePrice] WebSocket constructor fallo', err);
      this.#scheduleReconnect();
      return;
    }
    this.#ws = ws;

    ws.onopen = () => {
      this.status = 'open';
      this.#reconnectAttempt = 0;
      // Re-subscribe inmediato al reconnect (el server no recuerda symbols del cliente anterior).
      this.#sendSubscribe();
    };

    ws.onmessage = (ev) => {
      this.#handleMessage(ev.data);
    };

    ws.onerror = (err) => {
      console.warn('[livePrice] WS error', err);
    };

    ws.onclose = () => {
      if (this.#ws === ws) this.#ws = null;
      this.#scheduleReconnect();
    };
  }

  #handleMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let msg: ServerMessage;
    try {
      msg = JSON.parse(raw) as ServerMessage;
    } catch {
      return;
    }
    if (!msg || typeof msg.type !== 'string') return;

    if (msg.type === 'STATUS') {
      this.upstreamStatus = msg.upstream;
      return;
    }

    if (msg.type === 'CANDLE') {
      const c = msg.candle;
      if (!c || typeof c.symbol !== 'string' || typeof c.close !== 'number') return;
      const next = new Map(this.prices);
      next.set(c.symbol, {
        symbol: c.symbol,
        price: c.close,
        timestamp: c.timestamp ? new Date(c.timestamp) : new Date(),
      });
      this.prices = next;
      this.lastMessage = new Date();
    }
  }

  #scheduleReconnect(): void {
    if (this.#stopped) return;
    this.status = 'reconnecting';
    const attempt = this.#reconnectAttempt++;
    const delay = Math.min(RECONNECT_INITIAL_MS * 2 ** attempt, RECONNECT_MAX_MS);
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = setTimeout(() => this.#connect(), delay);
  }
}

export const livePriceStore = new LivePriceStore();

// ============================================================
// Unrealized PnL helper. Cierra audit v1 §6 fix #1:
//   - NO usar default a 20 cuando valuePerPoint es null.
//   - Si valuePerPoint es null o price no esta disponible, devolvemos null
//     (la UI muestra "N/A").
// ============================================================

export interface UnrealizedPnLInput {
  side: 'long' | 'short' | null;
  entryPrice: number | null;
  quantity: number | null;
  valuePerPoint: number | null;
  livePrice: number | null;
}

export function unrealizedPnL(input: UnrealizedPnLInput): number | null {
  const { side, entryPrice, quantity, valuePerPoint, livePrice } = input;
  if (
    side == null ||
    entryPrice == null ||
    quantity == null ||
    valuePerPoint == null ||
    livePrice == null
  ) {
    return null;
  }
  const priceChange = livePrice - entryPrice;
  const sign = side === 'long' ? 1 : -1;
  return priceChange * quantity * valuePerPoint * sign;
}

// ============================================================
// Reason why unrealizedPnL returned null. Mantenido en sync con la
// guarda de arriba, para que la UI muestre un mensaje accionable en
// lugar de "N/A" pelado.
//
// Codigos:
//   - 'waiting-price': sub WS OK pero todavia no llego el primer tick
//   - 'no-trade': falta algun campo del trade (side, entry, qty)
//   - 'no-vpp': falta valuePerPoint (trade sin dato + sin fallback config)
//   - null: se puede computar; usar `unrealizedPnL()`
//
// Decision: priorizamos 'no-vpp' por encima de 'waiting-price' porque
// es un problema sistemico (no se va a resolver con el proximo tick),
// y el usuario tiene que enterarse.
// ============================================================

export type UnrealizedPnLReason = 'waiting-price' | 'no-trade' | 'no-vpp' | null;

export function unrealizedPnLReason(input: UnrealizedPnLInput): UnrealizedPnLReason {
  const { side, entryPrice, quantity, valuePerPoint, livePrice } = input;
  if (side == null || entryPrice == null || quantity == null) return 'no-trade';
  if (valuePerPoint == null) return 'no-vpp';
  if (livePrice == null) return 'waiting-price';
  return null;
}
